import { isCliImmediateShutdownRequested } from '../../../runtime/cli-process-interrupt.js';
import { closeAllCodeNavCacheSqliteConnections } from '../../../sqlite/code-nav-cache/database.js';
import { maybeLogCodeNavCacheWorkerError } from '../../../sqlite/code-nav-cache/code-nav-cache-log.js';
import { CliSqliteInterrupted } from '../../../sqlite/sqlite-errors.js';
import { handleIndexWorkerMessage } from './index-handlers.js';
import { postSafe } from './post-message.js';
import { handleQueueWorkerMessage } from './queue-handlers.js';
import { handleSetupWorkerMessage } from './setup-handlers.js';
import type { SymbolIndexWorkerInMessage, SymbolIndexWorkerPort } from './protocol.js';
import {
  isSymbolIndexWorkerShutdownRequested,
  requestSymbolIndexWorkerShutdown,
} from './shutdown-state.js';

let activeWorkerHandlers = 0;

async function waitForActiveWorkerHandlersIdle(pollMs = 10): Promise<void> {
  while (activeWorkerHandlers > 0) {
    await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
  }
}

export async function handleRequestMessage(
  port: SymbolIndexWorkerPort,
  msg: SymbolIndexWorkerInMessage,
): Promise<void> {
  if (msg.type === 'shutdown') {
    requestSymbolIndexWorkerShutdown();
    await waitForActiveWorkerHandlersIdle();
    closeAllCodeNavCacheSqliteConnections();
    postSafe(port, { type: 'shutdownComplete' });
    return;
  }

  if (isCliImmediateShutdownRequested() || isSymbolIndexWorkerShutdownRequested()) return;

  activeWorkerHandlers += 1;
  try {
    switch (msg.type) {
      case 'enqueue':
      case 'markLatestActive':
      case 'deferBackground':
      case 'fileBrowserActivity':
        handleQueueWorkerMessage(msg);
        return;
      case 'registerParent':
      case 'scheduleWarmup':
        try {
          await handleSetupWorkerMessage(msg);
        } catch (err) {
          if (!(err instanceof CliSqliteInterrupted)) {
            maybeLogCodeNavCacheWorkerError(err instanceof Error ? err.message : String(err));
          }
        }
        return;
      case 'buildFull':
      case 'updateFile':
      case 'removeFile':
      case 'ensureFile':
        await handleIndexWorkerMessage(port, msg);
        return;
      default:
        return;
    }
  } catch (err) {
    if (err instanceof CliSqliteInterrupted) return;
    throw err;
  } finally {
    activeWorkerHandlers = Math.max(0, activeWorkerHandlers - 1);
  }
}
